import {HttpClient, HttpHeaders} from '@angular/common/http';
import {firstValueFrom, Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {ProductService} from './product.service';
import {RegionsService} from './regions.service';
import {ParametersService} from './parameters.service';
import {MapService} from './map.service';
import {EventsService} from './events.service';
import {SettingsService} from './settings.service';
import {UserService} from "./user.service";
import {AEDrequest, Product, ProductParameter, UploadId} from "../model/product";

interface Basket {
  positionlist: {
    product: {
      productparameters: {id: string; value: string} []
    }
  } []
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  items: Product[] = [];
  product: Product | undefined;
  aedRequestList: AEDrequest | undefined;

  totalSelectionArea = 0;

  constructor(
    private settings: SettingsService,
    private events: EventsService,
    private productService: ProductService,
    private regionService: RegionsService,
    private parameterService: ParametersService,
    private mapService: MapService,
    private userService: UserService,
    private http: HttpClient) {
    this.events.subscribe('total-area:change', (area) => this.totalSelectionArea = area / 1e6);
    this.events.subscribe('user:restore', () => this.onUserLogin());
  }

  async onUserLogin() {
    // Get cart items of the user
    const basket: Basket = await this.getShoppingBasket();
    // The positionlist is null, if the shopping cart is empty
    if (basket.positionlist !== null) {
      basket.positionlist.forEach(val => {
        // The items are added to the shopping basket
        if (val.product.productparameters.length > 0) {
          const parameters = val.product.productparameters.find(entry => entry.id === 'FE-Auftrag');
          if (parameters) {
            const valueJSON: Product = JSON.parse(parameters.value.replace(/\\"/g, '\''));
            this.addProductToItems(valueJSON);
          }
        }
      });
    }
  }

  /**
   * A product will be created. Finally it will be added to the shopping cart.
   * @returns validateProduct
   */
  async createProduct() {
    const validateProduct = {type: 'success', message: <string[]>[]};
    const shoppingBasket = await this.getShoppingBasket() as any;
    if (!shoppingBasket || !shoppingBasket.orderid) {
      validateProduct.type = 'danger';
      validateProduct.message.push('Es konnte kein Warenkorb gefunden werden.');
      return validateProduct;
    } else {
      const basketId = shoppingBasket.orderid;
      this.product = new Product();
      this.aedRequestList = new AEDrequest();
      
      const date = new Date();
      const year = date.getFullYear().toString();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      const hours = ('0' + date.getHours()).slice(-2);
      const minutes = ('0' + date.getMinutes()).slice(-2);
      const seconds = ('0' + date.getSeconds()).slice(-2);
      // orderId is made up of basket's orderid and YYMMDDhhmmss
      this.product.orderId = basketId + '_' + year + month + day + hours + minutes + seconds;
      this.product.productType = this.productService.getSelectedProductId();
      if (this.product.productType === '') {
        validateProduct.type = 'warning';
        validateProduct.message.push('Bitte wählen Sie ein Produkt aus.');
      }

      this.product.bundesland = this.regionService.getStatesKN();
      this.product.bezirksregierung = this.regionService.getDistrictsKN();
      this.product.kreis = this.regionService.getCountiesKN();
      this.product.kommune = this.regionService.getKommunenKN();

      this.product.selectedRegions = this.regionService.getTrueValues(this.regionService.getSelectedDistricts())
        .concat(this.regionService.getTrueValues(this.regionService.getSelectedStates()),
          this.regionService.getTrueValues(this.regionService.getSelectedKommunen()),
          this.regionService.getTrueValues(this.regionService.getSelectedCounties()));

      // TODO extend validation by gemarkung and fluren
      // TODO selectedRegions should not be part of the shopping cart item
      this.product.drawnGeometry = this.mapService.getGeojson();
      if (this.product.bundesland.length === 0
        && this.product.bezirksregierung.length === 0
        && this.product.kreis.length === 0
        && this.product.kommune.length === 0
        && this.product.drawnGeometry === null) {
        validateProduct.type = 'warning';
        validateProduct.message.push('Bitte wählen Sie eine Geometrie aus.');
      }

      if (this.totalSelectionArea < 50) {
        validateProduct.type = 'warning';
        validateProduct.message.push('Die gewählte Geometrie ist zu klein. Bitte wählen Sie eine Fläche von mindestens 50km² aus.');
      }

      const params = this.parameterService.getParams();
      this.product.orderName = params.jobName;
      if (!this.product.orderName) {
        validateProduct.type = 'warning';
        validateProduct.message.push('Bitte geben Sie eine Auftragsbezeichnung an.');
      }

      this.product.automaticTraining = params.checkboxAutomaticTraining;
      this.product.lbQuality = params.checkboxLB;

      this.product.startDate = params.startDate;
      this.product.endDate = params.endDate;
      if (!this.product.endDate) {
        validateProduct.type = 'warning';
        validateProduct.message.push('Bitte wählen Sie eine Zeitspanne aus.');
      }

      this.product.dopUploadID = params.dopUploadID;
      this.product.alkisUploadID = params.alkisUploadID;
      this.product.ndomUploadID = params.ndomUploadID;
      this.product.landcoverUploadID = params.landcoverUploadID;
      this.product.trainingDataUploadID = params.trainingDataUploadID;
      this.product.validationDataUploadID = params.validationDataUploadID;
      this.product.cdFilterUploadID = params.cdFilterUploadID;
      this.product.cdMaskUploadID = params.cdMaskUploadID;

      this.product.matrix = params.matrix;
      if (this.productService.getSelectedProductId() === 'LB') {
        this.product.matrix = null;
      }

      return validateProduct;
    }
  }

  /**
   * Sets the selected district
   * @param selectedDistrict selected district
   * @returns void
   */
  setDistrict(selectedDistrict: string[]) {
    if (this.product) {
      this.product.bezirksregierung = selectedDistrict;
    }
  }

  /**
   * Sets the selected product
   * @param productName selected product
   * @returns void
   */
  setProductName(productName: string) {
    if (this.product) {
      this.product.productType = productName;
    }
  }

  /**
   * Get product name
   * @returns name of the product
   */
  getProductName(): string | undefined {
    if (this.product) {
      return this.product.productType;
    } else {
      return undefined;
    }
  }

  /**
   * Set automatic training to true or false
   * @param state selected state
   * @returns void
   */
  setAutomaticTraining(state: boolean) {
    if (this.product) {
      this.product.automaticTraining = state;
    }
  }

  /**
   * Sets the start date
   * @param date start date
   * @returns void
   */
  setStartDate(date: NgbDate) {
    if (this.product) {
      this.product.startDate = date;
    }
  }

  /**
   * Sets the end date
   * @param date end date
   * @returns void
   */
  setEndDate(date: NgbDate) {
    if (this.product) {
      this.product.endDate = date;
    }
  }

  /**
   * Sets the upload id
   * @param id upload id
   * @returns void
   */
  // setUploadID(id: string) {
  //   this.product.insituUploadID = id;
  // }

  /**
   * Sets the matrix. The matrix is necessary for calculating the change analysis
   * @param matrix ueberfuehrungsmatrix
   * @returns void
   */
  setMatrix(matrix: object) {
    if (this.product) {
      this.product.matrix = matrix;
    }
  }

  /**
   * Sets the geometry drawn by the user
   * @param geom geometry
   * @returns void
   */
  setGeometry(geom: any) {
    if (this.product) {
      this.product.drawnGeometry = geom;
    }
  }

  /**
   * Adds the product to the list of products
   * @param product single product
   * @throws Error
   * @returns void
   */
  addProductToItems(product?: Product) {
    if (product) {
      // A product retrieved after login is added to the shopping cart
      this.items.push(product);
      // In this case a new product is added to the shopping cart.
      // The new product item is member of this class
    } else if (this.product) {
      this.items.push(this.product);
    } else {
      throw new Error('No product specified.');
    }
  }

  /**
   * Server request: adds a single product to the shopping cart of the user
   * @returns Observable
   */
  async addToCart() {
    if (this.product && this.aedRequestList) {
      const productName = this.productService.getProductName(this.product.productType);
      if (!productName) {
        throw new Error('No productname specified.');
      } else {
        this.aedRequestList.requestlist[0].product.description = this.product.orderName;
        this.aedRequestList.requestlist[0].product.fselection = productName;
        this.aedRequestList.requestlist[0].product.productparameters[0].value = JSON.stringify(this.product);
        this.aedRequestList.requestlist[0].product.productparameters[1].valueName = this.product.orderId;
        this.aedRequestList.requestlist[0].product.productparameters[1].customerOrderID = this.product.orderId;

        // add productparameter for current date
        const dateParam: ProductParameter = {
          id: 'Zeitraum',
          isUsed: true,
          showInShoppingbasket: true,
          isPricingParam: false,
          valueName: this.formatDate(this.product.startDate) + ' - ' + this.formatDate(this.product.endDate),
          isCostParam: false,
          isConfigurationParam: true,
          name: 'Zeitraum',
          value: ''
        };
        this.aedRequestList.requestlist[0].product.productparameters.push(dateParam);

        const regionsParam: ProductParameter = {
          id: 'Gewählte Gebiete',
          isUsed: true,
          showInShoppingbasket: true,
          isPricingParam: false,
          valueName: '',
          isCostParam: false,
          isConfigurationParam: true,
          name: 'Gewählte Gebiete',
          value: ''
        };
        if (this.product.selectedRegions.length > 0) {
          // add chosen areas OR
          regionsParam.valueName = this.product.selectedRegions.join(', ');
        } else if (this.product.drawnGeometry && this.product.drawnGeometry.geometry) {
          // add custom area OR
          regionsParam.valueName = 'frei gewählte/eigene Geometrie';
        }
        this.aedRequestList.requestlist[0].product.productparameters.push(regionsParam);

        // add upload ids
        const uploadIds: { id: UploadId; name: string }[] = [
          {id: UploadId.dopUploadID, name: 'DOP Upload-ID'},
          {id: UploadId.alkisUploadID, name: 'ALKIS Upload-ID'},
          {id: UploadId.ndomUploadID, name: 'nDOM Upload-ID'},
          {id: UploadId.landcoverUploadID, name: 'Landbedeckung Upload-ID'},
          {id: UploadId.trainingDataUploadID, name: 'Manuelle Trainingsdaten Upload-ID'},
          {id: UploadId.validationDataUploadID, name: 'Validierungsdaten Upload-ID'},
          {id: UploadId.cdFilterUploadID, name: 'Filter Veränderungshinweissuche Upload-ID'},
          {id: UploadId.cdMaskUploadID, name: 'Maske Veränderungshinweissuche Upload-ID'}
        ];

        if (!uploadIds.every(entry => !this.product![entry.id])) {
          const uploadIdParam: ProductParameter = {
            id: 'Die Berechnung wird mit Daten durchgeführt, die über das Uploadportal hochgeladen wurden',
            isUsed: true,
            showInShoppingbasket: true,
            isPricingParam: false,
            valueName: '',
            isCostParam: false,
            isConfigurationParam: true,
            name: 'Die Berechnung wird mit Daten durchgeführt, die über das Uploadportal hochgeladen wurden',
            value: ''
          };
          uploadIds.forEach(entry => {
            if (this.product![entry.id]) {
              if (uploadIdParam.valueName !== '') {
                uploadIdParam.valueName += ', ';
              }
              uploadIdParam.valueName += `${entry.name}: ${this.product![entry.id]}`;
            }
          });
          this.aedRequestList.requestlist[0].product.productparameters.push(uploadIdParam);
        }

        const token = await this.userService.getToken();
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const positionUrl = await this.settings.get('position-url');
        return this.http.post(`${positionUrl}?Token=${token}`, JSON.stringify(this.aedRequestList), {headers}).toPromise();
      }
    } else {
      throw new Error('Product and/or request list not specified.')
    }
  }

  formatDate(date: NgbDate): string {
    const day = date.day < 10 ? `0${date.day}` : date.day.toString();
    const month = date.month < 10 ? `0${date.month}` : date.month.toString();
    return `${day}.${month}.${date.year}`;
  }

  /**
   * Orders a shopping cart
   * @returns Observable
   */
  async doOrder() {
    const token = await this.userService.getToken();
    const orderUrl = await this.settings.get('order-url');
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return firstValueFrom(this.http.post(`${orderUrl}?Token=${token}`, {headers}));
  }

  /**
   * After login the shopping cart is set to the local variable
   * @param cart shopping cart
   * @returns void
   */
  setCart(cart: Product[]) {
    this.items = cart;
  }

  /**
   * Get cart of the user
   * @returns shopping cart
   */
  getCart(): Product[] {
    return this.items;
  }

  /**
   * Clears the shopping cart
   * @returns void
   */
  clearCartItems() {
    this.items = [];
  }

  /**
   * Server request: deletes the cart
   * @returns Observable
   */
  async deleteCart() {
    const token = await this.userService.getToken();
    const basketUrl = await this.settings.get('basket-url');
    return firstValueFrom(this.http.delete(`${basketUrl}?Token=${token}`));
  }

  /**
   * Server request: returns the actual cart of the user
   * @returns Observable
   */
  async getShoppingBasket(): Promise<any> {
    const token = await this.userService.getToken();
    const basketUrl = await this.settings.get('basket-url');
    return firstValueFrom(this.http.get(`${basketUrl}?Token=${token}`));
  }

  /**
   * Deletes a single entry of the shopping cart
   * @param orderId unique order id
   * @returns Observable
   */
  async deleteShoppingPosition(orderId: any) {
    const token = await this.userService.getToken();
    const positionUrl = await this.settings.get('position-url');
    return firstValueFrom(this.http.delete(`${positionUrl}/${orderId}?Token=${token}`));
  }

  reset() {
    this.items = [];
    this.product = undefined;
    this.aedRequestList = undefined;
    this.totalSelectionArea = 0;
  }
}
